import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  Checkbox,
  CheckboxProps,
  Input,
  message,
  Popconfirm,
  Radio,
  Table,
  TableColumnsType,
  TableProps,
} from "antd";
import { IconActionKey, IconArrowDown, IconDownload2 } from "assets/svg";
import {
  COMPANY_MANAGE_FILTER_STATUS_TYPE,
  COMPANY_MANAGE_FILTER_SORT,
  COMPANY_FILTER_USER_TYPE,
} from "common/constants";
import { Company, CompanyFilter } from "common/interfaces";
import PaginateListMobile from "components/PaginateListMobile";
import CompanyManageItem from "components/CompanyManageItem";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import {
  useDeleteCompanyMutation,
  useDownloadCompanyCsvMutation,
  useGetCompaniesQuery,
  useUpdateCompanyMutation,
} from "reducers/companyApi";
import { parseErrorMessage } from "common/utils";
import { CompanyInfoStatusEnum } from "common/enums/CompanyInfoStatus.enum";
import { usePagination } from "common/hooks/usePagination";
import { UserTypeEnum, UserTypeToLabel } from "common/enums/UserType.enum";
import { useCommonContext } from "contexts/CommonContext";
import { PAGINATION } from "common/enums/Pagination.enum";
import { companyInfoStatusMap } from "common/helper/text.helper";
import CustomConfirmModal from "components/CustomConfirmModal";

const CompanyManagementPage = () => {
  const columns: TableColumnsType<Company> = [
    {
      title: "ユーザーID",
      dataIndex: "id",
      align: "center",
      render: (value: number, record: Company) => (
        <Link
          to={`/admin/company-management/${record.code}/view`}
          className="text-primary"
        >
          {record.code}
        </Link>
      ),
    },
    {
      title: "法人名",
      dataIndex: "company_name",
      align: "center",
    },
    {
      title: "担当者名",
      dataIndex: "contact_last_name",
      align: "center",
    },
    {
      title: "メールアドレス",
      dataIndex: "email",
      align: "center",
    },
    {
      title: "ユーザー種別",
      dataIndex: "type",
      align: "center",
      render: (value) => UserTypeToLabel[value as UserTypeEnum],
    },
    {
      title: "ステータス",
      dataIndex: "status",
      align: "center",
      render: (value) => companyInfoStatusMap[value],
    },
    {
      title: "操作",
      dataIndex: "id",
      align: "center",
      render: (value: number, record: Company) => (
        <>
          <Link
            to={`/admin/company-management/${record.code}/view`}
            className="text-primary mr-1.5"
          >
            詳細
          </Link>
          <button
            className="text-primary mr-1.5"
            onClick={() => onFrozen(record)}
          >
            {record.status === CompanyInfoStatusEnum.FROZEN ? "解除" : "凍結"}
          </button>
        </>
      ),
    },
  ];

  const { setHeaderTitle } = useCommonContext();
  const [showFilter, setShowFilter] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRowKeyMobile, setSelectedRowKeyMobile] = useState<string[]>(
    [],
  );
  const [searchValueDebounced, setSearchValueDebounced] = useState("");
  const [openConfirmModalFrozen, setOpenConfirmModalFrozen] = useState(false);
  const [openConfirmModalApprove, setOpenConfirmModalApprove] = useState(false);
  const [companyCode, setCompanyCode] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const {
    pagination,
    paginationMobile,
    onChangePage,
    reset: resetPagination,
    resetMobile: resetPaginationMobile,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const defaultValues: CompanyFilter = {
    search: "",
    type: [
      COMPANY_FILTER_USER_TYPE.SELLER.value,
      COMPANY_FILTER_USER_TYPE.BUYER.value,
    ],
    status: [
      COMPANY_MANAGE_FILTER_STATUS_TYPE.APPROVED.value,
      COMPANY_MANAGE_FILTER_STATUS_TYPE.FROZEN.value,
    ],
    sort: COMPANY_MANAGE_FILTER_SORT.NEWEST.value,
  };

  const { handleSubmit, control, watch, reset } = useForm<CompanyFilter>({
    defaultValues,
  });

  const companyList: CompanyFilter = watch();
  const prevCompanyList = useRef<CompanyFilter>(defaultValues);

  const {
    data: companyData,
    error: companyErr,
    refetch: refetchCompany,
  } = useGetCompaniesQuery({
    filter: {
      ...companyList,
      search: searchValueDebounced,
    },
    page: pagination.current,
  });

  const {
    data: companyMobileData,
    error: companyMobileErr,
    refetch: refetchCompanyMobile,
  } = useGetCompaniesQuery({
    filter: {
      ...companyList,
      search: searchValueDebounced,
    },
    page: paginationMobile.current,
  });

  const [updateCompany] = useUpdateCompanyMutation();
  const [deleteCompany] = useDeleteCompanyMutation();
  const [downloadCompanyCsv] = useDownloadCompanyCsvMutation();

  const plainTypeOptions = Object.values(COMPANY_FILTER_USER_TYPE).map(
    (o) => o.value,
  );
  const plainStatusOptions = Object.values(
    COMPANY_MANAGE_FILTER_STATUS_TYPE,
  ).map((o) => o.value);

  const [checkedTypeList, setCheckedTypeList] =
    useState<string[]>(plainTypeOptions);
  const [checkedStatusList, setCheckedStatusList] =
    useState<string[]>(plainStatusOptions);

  const checkAllType = plainTypeOptions.length === checkedTypeList.length;
  const indeterminateType =
    checkedTypeList.length > 0 &&
    checkedTypeList.length < plainTypeOptions.length;

  const checkAllStatus = plainStatusOptions.length === checkedStatusList.length;
  const indeterminateStatus =
    checkedStatusList.length > 0 &&
    checkedStatusList.length < plainStatusOptions.length;

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const rowSelection: TableProps<Company>["rowSelection"] = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ name: record.code }),
    hideSelectAll: true,
  };

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      const allKeys = companyData?.data?.items?.map((item) => item.code) ?? [];
      setSelectedRowKeys(allKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleCheckAllMobile = (checked: boolean) => {
    if (checked) {
      setSelectedRowKeyMobile(
        companyMobileData?.data?.items.map((item) => item.code) || [],
      );
    } else {
      setSelectedRowKeyMobile([]);
    }
  };

  const onUpdateCompany = async (status: number) => {
    await updateCompany({
      companyCode,
      status:
        status === CompanyInfoStatusEnum.FROZEN
          ? CompanyInfoStatusEnum.FROZEN
          : CompanyInfoStatusEnum.APPROVED,
    });
    setOpenConfirmModalFrozen(false);
    setOpenConfirmModalApprove(false);
    setCompanyCode("");
  };

  const onFrozen = (record: Company) => {
    if (record.status === CompanyInfoStatusEnum.FROZEN) {
      setOpenConfirmModalApprove(true);
    } else {
      setOpenConfirmModalFrozen(true);
    }
    setCompanyCode(record.code);
  };

  const downloadCsv = async () => {
    await downloadCompanyCsv({
      filter: companyList,
      fileName: "ユーザー管理.csv",
    });
  };

  const confirm = async () => {
    try {
      const keys = !isEmpty(selectedRowKeys)
        ? selectedRowKeys
        : !isEmpty(selectedRowKeyMobile)
          ? selectedRowKeyMobile
          : [];
      if (!isEmpty(keys)) {
        await deleteCompany(keys).unwrap();
        message.success("削除に成功しました");
        resetPagination();
        resetPaginationMobile();
      }
    } catch (err) {
      message.error(parseErrorMessage(err));
    }
  };

  const onReset = async () => {
    resetPagination();
    resetPaginationMobile();
    reset();
    setSearchValueDebounced("");
    setCheckedStatusList(plainStatusOptions);
    setCheckedTypeList(plainTypeOptions);
  };

  const onSubmit = async () => {
    resetPagination();
    resetPaginationMobile();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchValueDebounced(companyList.search);
    setTimeout(() => {
      refetchCompany();
      refetchCompanyMobile();
    }, 0);
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchValueDebounced(companyList.search);
    }, 2000);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [companyList.search]);

  useEffect(() => {
    if (
      JSON.stringify(prevCompanyList.current) !== JSON.stringify(companyList)
    ) {
      resetPagination();
      resetPaginationMobile();
      prevCompanyList.current = companyList;
    }
  }, [companyList]);

  useEffect(() => {
    if (pagination.current === PAGINATION.PAGE) {
      refetchCompany();
    }
  }, [pagination.current]);

  useEffect(() => {
    if (paginationMobile.current === PAGINATION.PAGE) {
      refetchCompanyMobile();
    }
  }, [paginationMobile.current]);

  useEffect(() => {
    if (companyData?.data?.meta?.total_items) {
      updateTotal(companyData.data.meta.total_items);
    }
  }, [companyData]);

  useEffect(() => {
    if (companyMobileData?.data?.meta?.total_items) {
      updateTotalMobile(companyMobileData.data.meta.total_items);
    }
  }, [companyMobileData]);

  useEffect(() => {
    if (companyErr) void message.error(parseErrorMessage(companyErr));
  }, [companyErr]);

  useEffect(() => {
    if (companyMobileErr)
      void message.error(parseErrorMessage(companyMobileErr));
  }, [companyMobileErr]);

  useEffect(() => {
    setHeaderTitle("ユーザー管理");
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full product-list">
      <div className="flex min-h-[752px] px-5 pt-5 md:p-[40px] pb-[100px] flex-col items-center gap-[30px] self-stretch">
        <div className="flex flex-col items-start gap-[10px] md:gap-[20px] self-stretch">
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                prefix={<IconActionKey />}
                placeholder="法人名、担当者名、メールアドレスなどで検索"
                className="w-full h-[60px] outline outline-[3px] outline-offset-[-3px] outline-primary"
              />
            )}
          />
          <div
            data-プロパティ1="base"
            className="inline-flex justify-start items-center cursor-pointer"
            onClick={toggleFilter}
          >
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              検索条件を追加
            </div>
            <IconArrowDown className="w-[20px] h-[20px] aspect-[1/1]" />
          </div>
          {showFilter && (
            <div className="self-stretch p-[20px] md:p-10 bg-primary-light rounded-xl inline-flex flex-col justify-center items-center md:gap-7">
              <div className="self-stretch justify-start text-primary text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                検索条件（フィルター）
              </div>
              <div className="self-stretch flex flex-col justify-start items-start">
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    ユーザー種別
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="type"
                      defaultValue={plainTypeOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedTypeList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainTypeOptions
                            : [];
                          setCheckedTypeList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateType}
                              onChange={onCheckAllChange}
                              checked={checkAllType}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(COMPANY_FILTER_USER_TYPE)}
                              value={checkedTypeList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    ステータス
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="status"
                      defaultValue={plainStatusOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedStatusList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainStatusOptions
                            : [];
                          setCheckedStatusList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateStatus}
                              onChange={onCheckAllChange}
                              checked={checkAllStatus}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(
                                COMPANY_MANAGE_FILTER_STATUS_TYPE,
                              )}
                              value={checkedStatusList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 flex-col md:flex-row inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    並び替え
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="sort"
                      render={({ field }) => (
                        <Radio.Group
                          {...field}
                          options={Object.values(COMPANY_MANAGE_FILTER_SORT)}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-auto flex-col-reverse md:flex-row inline-flex justify-start items-start gap-[10px] md:gap-5">
                <Button
                  block
                  type="primary"
                  htmlType="reset"
                  onClick={onReset}
                  className="min-w-full md:min-w-[340px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-lg font-bold"
                >
                  クリア
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  className="min-w-full md:min-w-[340px] min-h-14 bg-primary text-lg font-bold"
                >
                  検索する
                </Button>
              </div>
            </div>
          )}
          <div className="self-stretch flex-col-reverse md:flex-row inline-flex justify-between items-center gap-3.5">
            <div className="hidden self-stretch md:inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!companyData?.data?.items &&
                  selectedRowKeys.length === companyData.data.items.length &&
                  companyData.data.items.length > 0
                }
                indeterminate={
                  !!companyData?.data?.items &&
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length < companyData.data.items.length
                }
                onChange={(e) => handleCheckAll(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="md:hidden self-stretch inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!companyMobileData?.data?.items &&
                  selectedRowKeyMobile.length ===
                    companyMobileData.data.items.length &&
                  companyMobileData.data.items.length > 0
                }
                indeterminate={
                  !!companyMobileData?.data?.items &&
                  selectedRowKeyMobile.length > 0 &&
                  selectedRowKeyMobile.length <
                    companyMobileData.data.items.length
                }
                onChange={(e) => handleCheckAllMobile(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="flex flex-col-reverse md:flex-row gap-3.5">
              <Popconfirm
                title="タスクを削除する"
                description="このタスクを削除してもよろしいですか?"
                onConfirm={confirm}
                okText="はい"
                cancelText="いいえ"
                disabled={
                  isEmpty(selectedRowKeys) && isEmpty(selectedRowKeyMobile)
                }
              >
                <div
                  data-プロパティ1="text only"
                  className="flex justify-center md:justify-start items-center cursor-pointer"
                >
                  <div className="justify-start text-blue-900 text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                    <>チェックした項目を削除</>
                  </div>
                </div>
              </Popconfirm>
              <Button
                type="primary"
                className="h-10 min-w-24 bg-primary"
                icon={
                  <IconDownload2 className="w-[20px] h-[20px] aspect-[1/1]" />
                }
                onClick={downloadCsv}
              >
                CSVデータをダウンロード
              </Button>
            </div>
          </div>
          <Table<Company>
            className="w-full hidden md:block"
            rowKey="code"
            rowSelection={rowSelection}
            onChange={onChangePage}
            pagination={!isEmpty(companyData?.data?.items) ? pagination : false}
            columns={columns}
            dataSource={companyData?.data?.items || []}
          />
          <PaginateListMobile<Company>
            selectedRowKeyMobile={selectedRowKeyMobile}
            setSelectedRowKeyMobile={setSelectedRowKeyMobile}
            pagination={
              !isEmpty(companyData?.data?.items) ? paginationMobile : false
            }
            dataSource={companyMobileData?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <CompanyManageItem<Company>
                company={item}
                checked={checked}
                onSelect={onSelect}
              />
            )}
          />
        </div>
      </div>
      <CustomConfirmModal
        open={openConfirmModalFrozen}
        title="このユーザーを
          凍結してもよろしいですか？"
        description={`このユーザーのアカウントを凍結すると、
          ログイン・出品・購入・メッセージなど、
          全ての機能が停止します。\n`}
        note={`
          ※凍結状態は解除されるまで自動で復帰しません。
          ※現在の取引には影響する可能性があります。`}
        onOk={() => onUpdateCompany(CompanyInfoStatusEnum.FROZEN)}
        onCancel={() => setOpenConfirmModalFrozen(false)}
        okText="ユーザーを凍結する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
      <CustomConfirmModal
        open={openConfirmModalApprove}
        title="このユーザーの凍結を
          解除してもよろしいですか？"
        description={`凍結状態を解除すると、このユーザーは
          通常通りログインできるようになり、
          取引やチャットなど、すべての機能が再び利用可能になります。\n`}
        note={`
          ※現在の凍結理由や状況をご確認のうえ、慎重に対応してください。
          ※凍結解除は即時反映されます。`}
        onOk={() => onUpdateCompany(CompanyInfoStatusEnum.APPROVED)}
        onCancel={() => setOpenConfirmModalApprove(false)}
        okText="凍結を解除する"
        cancelText="戻る"
        okButtonBgColor="bg-primary"
      />
    </form>
  );
};

export default CompanyManagementPage;
